import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const FormatContext = createContext();

export const FormatProvider = ({ children }) => {
  const [formats, setFormats] = useState([]);
  const [systemFields, setSystemFields] = useState([]);
  const [standardClubs, setStandardClubs] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all formats (admin sees all, users see published only)
  const fetchFormats = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await axios.get('/api/v1/import-formats');
      setFormats(data.formats);
    } catch (error) {
      console.error('Error fetching formats:', error);
      setError(error.response?.data?.message || 'Error fetching formats');
    } finally {
      setLoading(false);
    }
  };

  // Fetch system field definitions
  const fetchSystemFields = async () => {
    try {
      const { data } = await axios.get('/api/v1/import-formats/system-fields');
      setSystemFields(data.fields);
      setStandardClubs(data.standardClubs);
    } catch (error) {
      console.error('Error fetching system fields:', error);
      setError(error.response?.data?.message || 'Error fetching system fields');
    }
  };

  // Initialize - fetch system fields once on mount
  useEffect(() => {
    fetchSystemFields();
  }, []);

  // Analyze uploaded CSV file
  const analyzeFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      setLoading(true);
      setError(null);
      const { data } = await axios.post('/api/v1/import-formats/analyze', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return data;
    } catch (error) {
      console.error('Error analyzing file:', error);
      const errorMsg = error.response?.data?.message || 'Error analyzing file';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Create new import format
  const createFormat = async (formatData) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await axios.post('/api/v1/import-formats', formatData);
      await fetchFormats(); // Refresh list
      return data;
    } catch (error) {
      console.error('Error creating format:', error);
      const errorMsg = error.response?.data?.message || 'Error creating format';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Update existing format
  const updateFormat = async (id, formatData) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await axios.put(`/api/v1/import-formats/${id}`, formatData);
      await fetchFormats(); // Refresh list
      return data;
    } catch (error) {
      console.error('Error updating format:', error);
      const errorMsg = error.response?.data?.message || 'Error updating format';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Delete format
  const deleteFormat = async (id) => {
    try {
      setLoading(true);
      setError(null);
      await axios.delete(`/api/v1/import-formats/${id}`);
      await fetchFormats(); // Refresh list
    } catch (error) {
      console.error('Error deleting format:', error);
      const errorMsg = error.response?.data?.message || 'Error deleting format';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Toggle publish status
  const togglePublish = async (id, isPublished) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await axios.put(`/api/v1/import-formats/${id}/publish`, { isPublished });
      await fetchFormats(); // Refresh list
      return data;
    } catch (error) {
      console.error('Error toggling publish status:', error);
      const errorMsg = error.response?.data?.message || 'Error updating publish status';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Match file to existing formats
  const matchFormat = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      setLoading(true);
      setError(null);
      const { data } = await axios.post('/api/v1/import-formats/match', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return data;
    } catch (error) {
      console.error('Error matching format:', error);
      const errorMsg = error.response?.data?.message || 'Error matching format';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Get single format by ID
  const getFormatById = async (id) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await axios.get(`/api/v1/import-formats/${id}`);
      return data.format;
    } catch (error) {
      console.error('Error fetching format:', error);
      const errorMsg = error.response?.data?.message || 'Error fetching format';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormatContext.Provider value={{
      formats,
      systemFields,
      standardClubs,
      loading,
      error,
      fetchFormats,
      analyzeFile,
      createFormat,
      updateFormat,
      deleteFormat,
      togglePublish,
      matchFormat,
      getFormatById
    }}>
      {children}
    </FormatContext.Provider>
  );
};

export const useFormats = () => {
  const context = useContext(FormatContext);
  if (!context) {
    throw new Error('useFormats must be used within a FormatProvider');
  }
  return context;
};

export default FormatContext;
